import type {
  ContactActivityPagedResponseApi,
  ContactApi,
  ContactAttributeApi,
  ContactListMembershipApi,
  ContactSummaryApi,
  ConsentApi,
  PageInfoApi,
} from '../types/contacts-api.types';
import type { ContactActivityPagingResult } from '../types/contact-activity.types';
import type {
  Contact,
  ContactAttributeValue,
  ContactConsent,
  ContactListMembership,
  ContactsPagingResult,
} from '../types/contacts.types';
import { mapContactActivityEventApi } from './contact-activity.utils';

export const mapContactActivityRestResponse = (
  data: ContactActivityPagedResponseApi | undefined
): ContactActivityPagingResult => {
  const nextCursor = data?.pageInfo?.nextCursor?.trim() || undefined;
  const items = Array.isArray(data?.items) ? data.items : [];

  return {
    items: items.map(mapContactActivityEventApi).filter((event) => event.itemId),
    nextCursor,
    // A next page needs a cursor to ask for it with.
    hasNextPage: Boolean(data?.pageInfo?.hasNext && nextCursor),
  };
};

const isSubscribedStatus = (status: string | null | undefined): boolean => {
  const normalized = status?.trim().toLowerCase() ?? '';
  return normalized === 'subscribed' || normalized === 'active';
};

const mapConsentApi = (consent: ConsentApi | null | undefined): ContactConsent | null | undefined => {
  if (!consent) return undefined;

  return {
    Source: consent.source ?? undefined,
    Timestamp: consent.timestamp ?? undefined,
    SubscriptionChannel: consent.subscriptionChannel ?? undefined,
  };
};

const mapContactAttributeApi = (
  attribute: ContactAttributeApi
): ContactAttributeValue => ({
  PropertyKey: attribute.propertyKey ?? undefined,
  DataType: attribute.dataType ?? undefined,
  StringValue: attribute.stringValue ?? undefined,
  NumberValue: attribute.numberValue ?? undefined,
  BoolValue: attribute.boolValue ?? undefined,
  DateValue: attribute.dateValue ?? undefined,
  StringArrayValue: attribute.stringArrayValue ?? undefined,
});

const mapContactListsApi = (
  lists: ContactListMembershipApi[] | null | undefined
): ContactListMembership[] | undefined => {
  if (!Array.isArray(lists) || lists.length === 0) return undefined;

  const mapped = lists
    .map((list) => {
      const id = list.itemId?.trim() ?? '';
      const name = list.name?.trim() ?? '';
      if (!id && !name) return null;
      return { Id: id, Name: name || id };
    })
    .filter((list): list is ContactListMembership => list !== null);

  return mapped.length > 0 ? mapped : undefined;
};

export const mapContactApiToContact = (contact: ContactApi): Contact => {
  const emailSubscriptionStatus = contact.emailSubscriptionStatus ?? undefined;
  const smsSubscriptionStatus = contact.smsSubscriptionStatus ?? undefined;
  const lists = mapContactListsApi(contact.lists);

  return {
    ItemId: contact.itemId?.trim() ?? '',
    CreatedDate: contact.createdAt,
    LastUpdatedDate: contact.updatedAt,
    CreatedBy: contact.createdBy ?? undefined,
    LastUpdatedBy: contact.lastUpdatedBy ?? undefined,
    OrganizationId: contact.organizationId ?? undefined,
    Language: contact.language ?? undefined,
    Tags: contact.tags ?? undefined,
    ...(lists ? { Lists: lists } : {}),
    Email: contact.email ?? undefined,
    FirstName: contact.firstName ?? undefined,
    LastName: contact.lastName ?? undefined,
    Phone: contact.phone ?? undefined,
    Source: contact.source ?? undefined,
    SourceClient: contact.sourceClient ?? undefined,
    Locale: contact.locale ?? undefined,
    EmailSubscriptionStatus: emailSubscriptionStatus,
    SmsSubscriptionStatus: smsSubscriptionStatus,
    EmailSuppressed: contact.emailSuppressed === true ? true : undefined,
    SmsSuppressed: contact.smsSuppressed === true ? true : undefined,
    Consent: mapConsentApi(contact.consent),
    IsDeleted: contact.isDeleted ?? undefined,
    Attributes: Array.isArray(contact.attributes)
      ? contact.attributes.map(mapContactAttributeApi)
      : undefined,
    IsNewsLetterSubscribed: isSubscribedStatus(emailSubscriptionStatus),
    IsMarketingCampaignSubscribed: isSubscribedStatus(smsSubscriptionStatus),
  };
};

export const mapContactSummaryToContact = (summary: ContactSummaryApi): Contact => {
  const emailSubscriptionStatus = summary.emailSubscriptionStatus ?? undefined;
  const smsSubscriptionStatus = summary.smsSubscriptionStatus ?? undefined;
  const lists = mapContactListsApi(summary.lists);

  return {
    ItemId: summary.itemId?.trim() ?? '',
    Email: summary.email ?? undefined,
    FirstName: summary.firstName ?? undefined,
    LastName: summary.lastName ?? undefined,
    Phone: summary.phone ?? undefined,
    Source: summary.source ?? undefined,
    SourceClient: summary.sourceClient ?? undefined,
    EmailSubscriptionStatus: emailSubscriptionStatus,
    SmsSubscriptionStatus: smsSubscriptionStatus,
    EmailSuppressed: summary.emailSuppressed === true ? true : undefined,
    SmsSuppressed: summary.smsSuppressed === true ? true : undefined,
    CreatedDate: summary.createdAt,
    LastUpdatedDate: summary.updatedAt,
    ...(lists ? { Lists: lists } : {}),
    Attributes: Array.isArray(summary.attributes)
      ? summary.attributes.map(mapContactAttributeApi)
      : undefined,
    IsNewsLetterSubscribed: isSubscribedStatus(emailSubscriptionStatus),
    IsMarketingCampaignSubscribed: isSubscribedStatus(smsSubscriptionStatus),
  };
};

export const mapContactsRestResponse = (
  items: ContactSummaryApi[] | null | undefined,
  pageInfo: PageInfoApi | undefined,
  paging: { pageNo: number; pageSize: number }
): ContactsPagingResult => {
  const pageSize = pageInfo?.limit ?? paging.pageSize;
  const offset = pageInfo?.offset ?? (paging.pageNo - 1) * pageSize;
  const totalCount = Number(pageInfo?.total ?? 0);
  const pageNo = pageSize > 0 ? Math.floor(offset / pageSize) + 1 : paging.pageNo;
  const totalPages = pageSize > 0 ? Math.ceil(totalCount / pageSize) : 0;

  return {
    items: Array.isArray(items) ? items.map(mapContactSummaryToContact) : [],
    totalCount,
    pageNo,
    pageSize,
    totalPages,
    hasNextPage: Boolean(pageInfo?.hasNext),
    hasPreviousPage: Boolean(pageInfo?.hasPrev),
    // Passed through verbatim, not coalesced to 0: the backend omits it when the request narrowed
    // nothing, and `undefined` is what tells the footer to leave off "(filtered from N)" entirely.
    totalUnfiltered: pageInfo?.totalUnfiltered,
  };
};
